import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { useLanguage } from '@/context/LanguageContext';
import { useCurrency } from '@/context/CurrencyContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  FlaskConical, 
  LayoutDashboard, 
  Calendar, 
  FileText, 
  ClipboardList,
  User,
  Settings,
  LogOut,
  Bell,
  Menu,
  X,
  ChevronRight,
  Search,
  Users,
  BarChart3,
  TestTubes,
  Wallet,
  Moon,
  Sun,
  Globe,
  PenLine,
  FileSpreadsheet,
  CreditCard,
  Package,
  UserPlus
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { language, setLanguage, languages, t } = useLanguage();
  const { currency, setCurrency, currencies, formatPrice } = useCurrency();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications`);
      setNotifications(res.data.slice(0, 10));
      setUnreadCount(res.data.filter(n => !n.read).length);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (e) { /* silent */ }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const patientLinks = [
    { href: '/dashboard', label: t('dashboard'), icon: LayoutDashboard },
    { href: '/tests', label: t('tests'), icon: Search },
    { href: '/bookings', label: t('bookings'), icon: Calendar },
    { href: '/results', label: t('results'), icon: FileText },
    { href: '/medical-records', label: 'Medical Records', icon: ClipboardList },
    { href: '/wallet', label: t('wallet'), icon: Wallet },
    { href: '/profile', label: t('profile'), icon: User },
    { href: '/settings', label: t('settings'), icon: Settings },
  ];

  const labTechLinks = [
    { href: '/dashboard', label: t('dashboard'), icon: LayoutDashboard },
    { href: '/admin/result-entry', label: 'Enter Results', icon: PenLine },
    { href: '/admin/inventory', label: 'Inventory', icon: Package },
    { href: '/bookings', label: 'All Bookings', icon: Calendar },
    { href: '/results', label: t('results'), icon: FileText },
    { href: '/profile', label: t('profile'), icon: User },
    { href: '/settings', label: t('settings'), icon: Settings },
  ];

  const labScientistLinks = [
    { href: '/dashboard', label: t('dashboard'), icon: LayoutDashboard },
    { href: '/admin/result-entry', label: 'Enter Results', icon: PenLine },
    { href: '/admin/templates', label: 'Test Templates', icon: FileSpreadsheet },
    { href: '/admin/inventory', label: 'Inventory', icon: Package },
    { href: '/bookings', label: 'All Bookings', icon: Calendar },
    { href: '/results', label: t('results'), icon: FileText },
    { href: '/profile', label: t('profile'), icon: User },
    { href: '/settings', label: t('settings'), icon: Settings },
  ];

  const receptionistLinks = [
    { href: '/dashboard', label: t('dashboard'), icon: LayoutDashboard },
    { href: '/receptionist', label: 'Front Desk', icon: UserPlus },
    { href: '/admin/payments', label: 'Pending Payments', icon: CreditCard },
    { href: '/tests', label: 'Test Catalog', icon: TestTubes },
    { href: '/bookings', label: 'All Bookings', icon: Calendar },
    { href: '/profile', label: t('profile'), icon: User },
    { href: '/settings', label: t('settings'), icon: Settings },
  ];

  const adminLinks = [
    { href: '/dashboard', label: t('dashboard'), icon: LayoutDashboard },
    { href: '/admin', label: 'Analytics', icon: BarChart3 },
    { href: '/admin/users', label: 'Manage Users', icon: Users },
    { href: '/admin/tests', label: 'Test Price List', icon: TestTubes },
    { href: '/admin/payments', label: 'Pending Payments', icon: CreditCard },
    { href: '/admin/inventory', label: 'Inventory', icon: Package },
    { href: '/admin/result-entry', label: 'Enter Results', icon: PenLine },
    { href: '/admin/templates', label: 'Test Templates', icon: FileSpreadsheet },
    { href: '/receptionist', label: 'Front Desk', icon: UserPlus },
    { href: '/tests', label: 'Test Catalog', icon: Search },
    { href: '/bookings', label: 'All Bookings', icon: Calendar },
    { href: '/results', label: t('results'), icon: FileText },
    { href: '/medical-records', label: 'Medical Records', icon: ClipboardList },
    { href: '/wallet', label: t('wallet'), icon: Wallet },
    { href: '/settings', label: t('settings'), icon: Settings },
  ];

  const getNavLinks = () => {
    switch(user?.role) {
      case 'admin': return adminLinks;
      case 'lab_tech': return labTechLinks;
      case 'lab_scientist': return labScientistLinks;
      case 'receptionist': return receptionistLinks;
      default: return patientLinks;
    }
  };

  const navLinks = getNavLinks();

  const isActive = (href) => location.pathname === href;

  const Sidebar = ({ mobile = false }) => (
    <div className={`flex flex-col h-full ${mobile ? 'pt-4' : ''}`}>
      {/* Logo */}
      <div className="px-4 pb-6">
        <Link to="/" className="flex items-center gap-2 group" data-testid="sidebar-logo">
          <img 
            src="https://customer-assets.emergentagent.com/job_labresult-hub-1/artifacts/td8pjg41_FACTUAL%20LOGO.png" 
            alt="Factual Medical Logo" 
            className="w-10 h-10 rounded-full object-cover"
          />
          <div className="flex flex-col">
            <span className="text-sm font-bold text-foreground tracking-tight leading-tight">Factual Medical</span>
            <span className="text-xs text-muted-foreground leading-tight">Diagnostic Centre</span>
          </div>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 space-y-1">
        {navLinks.map((link) => {
          const Icon = link.icon;
          return (
            <Link
              key={link.href}
              to={link.href}
              data-testid={`sidebar-link-${link.label.toLowerCase().replace(' ', '-')}`}
              onClick={() => mobile && setSidebarOpen(false)}
              className={`sidebar-link ${isActive(link.href) ? 'sidebar-link-active' : ''}`}
            >
              <Icon className="w-5 h-5" />
              <span>{link.label}</span>
              {isActive(link.href) && <ChevronRight className="w-4 h-4 ml-auto" />}
            </Link>
          );
        })}
      </nav>

      {/* User Info */}
      <div className="p-4 border-t border-border mt-auto">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-sky-100 dark:bg-sky-900 rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-sky-700 dark:text-sky-300" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{user?.full_name}</p>
            <p className="text-xs text-muted-foreground capitalize">{user?.role?.replace('_', ' ')}</p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col border-r border-border bg-card">
        <Sidebar />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
          <div className="fixed inset-y-0 left-0 w-64 bg-card shadow-xl animate-fade-in">
            <div className="flex justify-end p-2">
              <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <Sidebar mobile />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Header */}
        <header className="sticky top-0 z-40 glass-header">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                className="lg:hidden"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-sidebar-toggle"
              >
                <Menu className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-lg font-semibold text-foreground">
                  {navLinks.find(l => isActive(l.href))?.label || t('dashboard')}
                </h1>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Theme Toggle */}
              <Button 
                variant="ghost" 
                size="icon"
                onClick={toggleTheme}
                data-testid="theme-toggle-btn"
              >
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>

              {/* Language Selector */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="language-dropdown">
                    <Globe className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {languages.map((lang) => (
                    <DropdownMenuItem 
                      key={lang.code} 
                      onClick={() => setLanguage(lang.code)}
                      className={language === lang.code ? 'bg-accent' : ''}
                    >
                      <span className="mr-2">{lang.flag}</span>
                      {lang.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Currency Selector */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-xs" data-testid="currency-dropdown">
                    {currencies.find(c => c.code === currency)?.symbol || '$'}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {currencies.map((curr) => (
                    <DropdownMenuItem 
                      key={curr.code} 
                      onClick={() => setCurrency(curr.code)}
                      className={currency === curr.code ? 'bg-accent' : ''}
                    >
                      <span className="mr-2">{curr.symbol}</span>
                      {curr.code}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Notifications */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                    <span className="font-semibold text-sm">Notifications</span>
                    {unreadCount > 0 && (
                      <button onClick={markAllRead} className="text-xs text-sky-600 hover:underline">Mark all read</button>
                    )}
                  </div>
                  {notifications.length === 0 ? (
                    <div className="px-3 py-6 text-center text-sm text-muted-foreground">No notifications yet</div>
                  ) : (
                    notifications.map(n => (
                      <DropdownMenuItem key={n.id} className={`flex flex-col items-start gap-0.5 px-3 py-2 cursor-default ${!n.read ? 'bg-sky-50 dark:bg-sky-900/20' : ''}`}>
                        <span className="text-sm font-medium text-foreground">{n.title}</span>
                        <span className="text-xs text-muted-foreground line-clamp-2">{n.message}</span>
                        <span className="text-[10px] text-muted-foreground/60">{n.created_at ? new Date(n.created_at).toLocaleDateString() : ''}</span>
                      </DropdownMenuItem>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="header-user-menu">
                    <div className="w-8 h-8 bg-sky-100 dark:bg-sky-900 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-sky-700 dark:text-sky-300" />
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2 border-b border-border">
                    <p className="text-sm font-medium text-foreground">{user?.full_name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuItem onClick={() => navigate('/wallet')}>
                    <Wallet className="w-4 h-4 mr-2" />
                    {t('wallet')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/settings')}>
                    <Settings className="w-4 h-4 mr-2" />
                    {t('settings')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
